import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {withPayloadType} from '@lib/utils';
import isToday from 'date-fns/isToday';
import parseISO from 'date-fns/parseISO';
import {getAreAllCheckInFlagsEnabled} from './selectors';
import {AppThunkApiConfig} from '../../createStore';
import {getIsCheckInFeatureEnabled} from '../../infrastructure/features/selectors';
import {GenericError, handleError} from '../../infrastructure/errorHandler';
import {mapiqApi, CheckInEventType, CheckInStatusDto} from '@lib/infrastructure';
import {getInitialState} from './initialState';

export const loadConnectionsCheckInStatus = createAsyncThunk<CheckInStatusDto[], void, AppThunkApiConfig>(
  'checkIn/loadConnectionsCheckedIn',
  async (_, {extra, getState}) => {
    if (!getIsCheckInFeatureEnabled(getState())) {
      return [];
    }

    const client = extra.apiClient;
    const api = mapiqApi(client);
    const connectionsStatus = await api.getConnectionsCheckInStatus();
    return connectionsStatus;
  },
);

export const loadCheckInStatus = createAsyncThunk<CheckInStatusDto, void, AppThunkApiConfig>(
  'checkIn/loadCheckedIn',
  async (_, {extra, getState}) => {
    if (!getIsCheckInFeatureEnabled(getState())) {
      const {status} = getInitialState();
      return status;
    }
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const checkInEvent = await api.getCheckInStatus();
    return checkInEvent;
  },
);

export const updateCheckInStatus = createAsyncThunk<
  CheckInStatusDto,
  {checkInStatus: CheckInStatusDto; workspaceReservation: {id?: string; startDate: string} | undefined},
  AppThunkApiConfig
>(
  'checkIn/updateCheckIn',
  async ({checkInStatus}, {extra, dispatch, rejectWithValue}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    try {
      await api.postCheckInStatus(checkInStatus);
    } catch {
      handleError(new GenericError(extra.i18n.lookup('error:GenericError')), dispatch);
      return rejectWithValue('Send check in status failed');
    }
    return checkInStatus;
  },
  {
    condition: ({checkInStatus, workspaceReservation}, {getState}) => {
      const state = getState();
      const previousCheckInStatus = state.data.checkIn.status;
      const shouldCheckInAfterWorkspaceReservation = state.data.checkIn.shouldCheckInAfterWorkspaceReservation;
      const areAllCheckinFlagsEnabled = getAreAllCheckInFlagsEnabled(getState());
      const isWorkspaceReservationToday = isToday(parseISO(workspaceReservation?.startDate ?? ''));
      const doesUserHaveAWorkspaceReservationToday = workspaceReservation ? isWorkspaceReservationToday : true;
      const isUserCheckingOutWithoutWorkspaceReservation = workspaceReservation
        ? true
        : checkInStatus.eventType === CheckInEventType.NotAtTheOffice;
      const isNewWorkspaceReservationAndShouldCheckin =
        workspaceReservation && !workspaceReservation.id ? shouldCheckInAfterWorkspaceReservation : true;
      const didCheckingDataChange =
        previousCheckInStatus.buildingId !== checkInStatus.buildingId ||
        previousCheckInStatus.eventType !== checkInStatus.eventType;
      return (
        areAllCheckinFlagsEnabled &&
        doesUserHaveAWorkspaceReservationToday &&
        isUserCheckingOutWithoutWorkspaceReservation &&
        didCheckingDataChange &&
        isNewWorkspaceReservationAndShouldCheckin
      );
    },
  },
);

export const updateCheckInStatusNew = createAsyncThunk<CheckInStatusDto, CheckInStatusDto, AppThunkApiConfig>(
  'checkIn/updateCheckInNew',
  async (checkInStatus, {extra, dispatch, rejectWithValue, getState}) => {
    if (!getAreAllCheckInFlagsEnabled(getState())) {
      const {status} = getInitialState();
      return status;
    }
    const client = extra.apiClient;
    const api = mapiqApi(client);
    await api.postCheckInStatus(checkInStatus);
    return checkInStatus;
  },
);

export const setShouldCheckInAfterWorkspaceReservation = createAction(
  'checkIn/shouldCheckInAfterWorkspaceReservationSet',
  withPayloadType<boolean>(),
);

export const showCheckInBottomSheet = createAction('checkIn/showBottomSheet', withPayloadType<boolean>());
